const TOKEN_STORAGE_KEY = 'token';
const USER_STORAGE_KEY = 'user';
const API_KEY_STORAGE_KEY = 'api_key';

export function getAuthToken(): string | null {
  if (typeof localStorage !== 'undefined') {
    const stored = localStorage.getItem(TOKEN_STORAGE_KEY);
    if (stored) return stored;
  }
  // httpOnly cookie (access_token) is sent automatically via credentials: 'include'
  // — no JS access needed. Return null to signal "try cookie-based auth".
  return null;
}

/**
 * Returns true if there may be a shared httpOnly cookie from another
 * KuberNodes app (e.g. dashboard). The cookie is not readable by JS,
 * so we probe the API to check.
 */
export function mayHaveSharedSession(): boolean {
  // If we have a localStorage token we're definitely authed locally.
  // If not, a shared httpOnly cookie might still exist — caller should
  // probe GET /auth/me with credentials: 'include' to find out.
  return typeof localStorage !== 'undefined' && !localStorage.getItem(TOKEN_STORAGE_KEY);
}

export function getApiKey(): string | null {
  if (typeof localStorage === 'undefined') return null;
  return localStorage.getItem(API_KEY_STORAGE_KEY);
}

export function buildAuthHeaders(headers: Record<string, string> = {}): Record<string, string> {
  const result = { ...headers };
  const token = getAuthToken();
  const apiKey = getApiKey();

  if (token) {
    result.Authorization = `Bearer ${token}`;
  }

  if (apiKey) {
    result['X-API-Key'] = apiKey;
  }

  return result;
}

export function setStoredUser(user: unknown): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
}

export function setAuthSession(token: string, user?: unknown): void {
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem(TOKEN_STORAGE_KEY, token);
  }
  // httpOnly cookie is set by the API automatically — no client-side cookie needed
  if (user !== undefined) {
    setStoredUser(user);
  }
}

export function clearAuthSession(): void {
  if (typeof localStorage !== 'undefined') {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    localStorage.removeItem(USER_STORAGE_KEY);
  }
  // httpOnly cookie is cleared by calling POST /auth/logout on the API
}
